export type PaymentProviderEnvironment = 'sandbox' | 'production';
export type PaymentProviderConnectionStatus = 'draft' | 'active' | 'disabled' | 'invalid';

export interface PaymentProviderCapabilities {
  store_method: boolean;
  verify_method: boolean;
  authorize: boolean;
  separate_capture: boolean;
  void: boolean;
  refund: boolean;
  webhooks: boolean;
}

export interface PaymentProviderFieldDefinition {
  key: string;
  label: string;
  required: boolean;
}

export interface PaymentProviderDefinition {
  provider_key: string;
  display_name: string;
  environments: PaymentProviderEnvironment[];
  publishable_fields: PaymentProviderFieldDefinition[];
  credential_fields: PaymentProviderFieldDefinition[];
  capabilities: PaymentProviderCapabilities;
}

export interface PaymentProviderConnection {
  connection_id: string;
  tenant_id: string;
  provider_key: string;
  environment: PaymentProviderEnvironment;
  display_name: string;
  status: PaymentProviderConnectionStatus;
  publishable_config: Record<string, string>;
  secret_configured: boolean;
  capabilities: PaymentProviderCapabilities;
  verified_at?: string | null;
  verification_error_code?: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface PaymentProviderRoute {
  route_id: string;
  tenant_id: string;
  connection_id: string;
  storefront_key?: string | null;
  channel_key?: string | null;
  currency: string;
  is_default: boolean;
  enabled: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export class PaymentAdminError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(paymentErrorMessage(code, status));
    this.name = 'PaymentAdminError';
  }
}

function paymentErrorMessage(code: string, status: number): string {
  const messages: Record<string, string> = {
    version_conflict: 'This payment configuration changed. Refresh and try again.',
    connection_not_verified: 'Verify the provider credentials before activating this connection.',
    credentials_required: 'Provider credentials must be configured first.',
    invalid_provider_configuration: 'The provider configuration is incomplete or invalid.',
    unsupported_provider: 'This payment provider is not supported by the service.',
    credential_store_unavailable: 'The secure credential store is unavailable.',
    forbidden: 'You do not have permission to manage payment providers.',
  };
  return messages[code] ?? `Payment provider request failed (${status}).`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    let code = 'request_failed';
    try {
      const body = await response.json() as { error?: string };
      code = body.error || code;
    } catch {
      // Preserve the generic safe error code.
    }
    throw new PaymentAdminError(response.status, code);
  }
  return response.json() as Promise<T>;
}

const connectionsPath = '/api/v1/payments/admin/provider-connections';
const routesPath = '/api/v1/payments/admin/provider-routes';

export const listPaymentProviderDefinitions = () => request<PaymentProviderDefinition[]>('/api/v1/payments/admin/provider-definitions');
export const listPaymentProviderConnections = () => request<PaymentProviderConnection[]>(connectionsPath);
export const listPaymentProviderRoutes = () => request<PaymentProviderRoute[]>(routesPath);

export const createPaymentProviderConnection = (input: { provider_key: string; environment: PaymentProviderEnvironment; display_name: string; publishable_config: Record<string, string> }) =>
  request<PaymentProviderConnection>(connectionsPath, { method: 'POST', body: JSON.stringify(input) });

export const updatePaymentProviderConnection = (connectionId: string, input: { version: number; display_name: string; publishable_config: Record<string, string> }) =>
  request<PaymentProviderConnection>(`${connectionsPath}/${encodeURIComponent(connectionId)}`, { method: 'PATCH', body: JSON.stringify(input) });

export const replacePaymentProviderCredentials = (connectionId: string, input: { version: number; credentials: Record<string, string> }) =>
  request<PaymentProviderConnection>(`${connectionsPath}/${encodeURIComponent(connectionId)}/credentials`, { method: 'POST', body: JSON.stringify(input) });

export const verifyPaymentProviderConnection = (connectionId: string) =>
  request<PaymentProviderConnection>(`${connectionsPath}/${encodeURIComponent(connectionId)}/verify`, { method: 'POST' });

export const setPaymentProviderConnectionStatus = (connectionId: string, action: 'activate' | 'disable', version: number) =>
  request<PaymentProviderConnection>(`${connectionsPath}/${encodeURIComponent(connectionId)}/${action}`, { method: 'POST', body: JSON.stringify({ version }) });

export const createPaymentProviderRoute = (input: { connection_id: string; storefront_key?: string; channel_key?: string; currency: string; is_default: boolean }) =>
  request<PaymentProviderRoute>(routesPath, { method: 'POST', body: JSON.stringify(input) });

export const disablePaymentProviderRoute = (routeId: string, version: number) =>
  request<PaymentProviderRoute>(`${routesPath}/${encodeURIComponent(routeId)}/disable`, { method: 'POST', body: JSON.stringify({ version }) });
